import { useAppDispatch } from '@core/redux/hooks';
import { RootState } from '@core/redux/reducers';
import { getBadgeCompetitionEventList } from '@features/gamefication/store/badge/getCompetitionEvent.builder';
import { saveFullBadgeEdition } from '@features/gamefication/store/badge/saveEdition.builder';
import {
  clearBadgeOnEdition,
  editingBadge,
} from '@features/gamefication/store/gamefication.slice';
import { FormInstance } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AnyObject } from 'yup';

import OpenNotification from 'src/app/components/antd/OpenNotification';
import {
  IBadgeRequestDto,
  IBadgeRuleCompetitionEventRequestDto,
} from 'src/core/gamefication/dto/badge-group/badge-request.dto';

export const useBadge = (badge: IBadgeRequestDto) => {
  const {
    status,
    badgeGroup,
    badgeOnFocusCompetitiveEventsList,
    badgeEditingId,
    badgeExpandedKey,
  } = useSelector((state: RootState) => state.gamification);

  const dispatch = useAppDispatch();

  const [badgeFormData, setBadgeFormData] = useState<IBadgeRequestDto>(badge);
  const [hasBadgeFormEditMode, setHasBadgeFormEditMode] = useState<boolean>(
    badge.id === badgeEditingId
  );

  const [badgeFormDataCache, setBadgeFormDataCache] =
    useState<IBadgeRequestDto>(badge);

  const formHookToCompetitionEvents = async <T extends AnyObject>(
    formHook: FormInstance<T>
  ) => {
    const changedValues = await formHook.validateFields();
    const normalizedValues: AnyObject = {};
    Object.keys(changedValues).forEach(key => {
      const propertyName = key.split('-')[0];
      const badgeGroupId: string = key.split('-')[1];

      if (!normalizedValues[badgeGroupId]) {
        normalizedValues[badgeGroupId] = {};
      }

      normalizedValues[badgeGroupId][propertyName] = changedValues[key];
    });

    const updatedValue = badgeFormData?.competitionEvents?.map(
      competitionEvent => {
        if (!competitionEvent?.id) return false;

        const groupId = competitionEvent.id;
        const updatedCompetitionEvent = {
          ...competitionEvent,
          ...normalizedValues[groupId],
        };

        return updatedCompetitionEvent;
      }
    );

    return updatedValue;
  };

  useEffect(() => {
    setBadgeFormData({
      ...badge,
      competitionEvents: badgeOnFocusCompetitiveEventsList,
    });

    setBadgeFormDataCache({
      ...badgeFormDataCache,
      competitionEvents: badgeOnFocusCompetitiveEventsList,
    });
  }, [badgeOnFocusCompetitiveEventsList]);

  useEffect(() => {
    if (badgeExpandedKey?.split('-')[1] !== badge.id.toString()) return;
    if (badge.isNew) return;
    dispatch(getBadgeCompetitionEventList(badgeFormData.id));
  }, [badgeExpandedKey]);

  const discardBadgeFormChanges = () => {
    dispatch(clearBadgeOnEdition());
    setBadgeFormData(badgeFormDataCache);
    setHasBadgeFormEditMode(false);
  };

  const editBadgeFormData = (value: IBadgeRequestDto) => {
    setBadgeFormData(value);
  };

  const enableEditMode = () => {
    dispatch(editingBadge(badge.id));
    setHasBadgeFormEditMode(true);
  };

  const editBadgeEvent = (
    badgeEvent: IBadgeRuleCompetitionEventRequestDto[]
  ) => {
    setBadgeFormData({
      ...badgeFormData,
      competitionEvents: badgeEvent,
    });
  };

  const saveAllBadge = async (
    eventReactHookForm: FormInstance<IBadgeRuleCompetitionEventRequestDto>
  ) => {
    if (!badgeGroup?.competition?.id) {
      OpenNotification(true, 'Competição não encontrada');
      return;
    }
    const badgeToSave = await formHookToCompetitionEvents(eventReactHookForm);

    dispatch(
      saveFullBadgeEdition({
        competitionId: badgeGroup.competition.id || 0,
        badgeGroupId: badgeGroup.id,
        badge: {
          ...badgeFormData,
          competitionEvents: badgeToSave,
        },
      })
    );

    if (status !== 'ERROR') {
      setBadgeFormDataCache(badgeFormData);
    }

    setHasBadgeFormEditMode(false);
  };

  return {
    badgeFormData,
    hasBadgeFormEditMode,
    saveAllBadge,
    enableEditMode,
    editBadgeEvent,
    editBadgeFormData,
    discardBadgeFormChanges,
  };
};
